import { dateOfDay } from './agenda-calendar'

/**
 * Les libellés de l'agenda, **nommés une fois** : l'en-tête de l'écran, les colonnes de la
 * semaine et les cases du mois doivent lire une date de la même façon.
 *
 * Les identifiants sont en anglais, les libellés en français — c'est la langue du métier et
 * celle de la maquette validée (`/CLAUDE.md`, garde-fou 2).
 */

const LOCALE = 'fr-FR'

/** « lundi 3 août » — l'en-tête d'une colonne de la semaine. */
export function dayLabel(day: string): string {
  const date = dateOfDay(day)

  return date === null
    ? day
    : date.toLocaleDateString(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' })
}

/** « lun. 3 » — la même colonne quand la place manque, sur une tablette en portrait. */
export function shortDayLabel(day: string): string {
  const date = dateOfDay(day)

  return date === null ? day : date.toLocaleDateString(LOCALE, { weekday: 'short', day: 'numeric' })
}

/** « 3 » — le numéro d'une case du mois. */
export function dayNumberLabel(day: string): string {
  return String(dateOfDay(day)?.getDate() ?? day)
}

/** « août 2026 » — le titre de la vue Mois. */
export function monthLabel(day: string): string {
  const date = dateOfDay(day)

  return date === null ? day : date.toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' })
}

/** « 3 → 8 août 2026 » — le titre de la vue Semaine, deux mois nommés quand elle les traverse. */
export function weekLabel(from: string, to: string): string {
  const start = dateOfDay(from)
  const end = dateOfDay(to)

  if (start === null || end === null) return `${from} → ${to}`

  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
  const startLabel = sameMonth
    ? String(start.getDate())
    : start.toLocaleDateString(LOCALE, { day: 'numeric', month: 'long' })

  return `${startLabel} → ${end.toLocaleDateString(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' })}`
}

/** Les heures d'une journée, pour ramener une ligne de gouttière au cadran. */
const HOURS_PER_DAY = 24

/**
 * « 08:00 » — le libellé d'une ligne de la gouttière, à partir de l'heure que rend `hoursOf`.
 *
 * ⚠️ **Ramené au cadran.** La gouttière se dérive des rendez-vous et son `toHour` déborde du jour
 * dès qu'un rendez-vous tardif y entre : à 23 h 30, le créneau d'une heure court jusqu'à 0 h 30,
 * donc `gutterOf` rend `toHour: 25` et `hoursOf` une dernière ligne numérotée 24. La ligne est
 * juste — c'est celle où la carte se pose —, son numéro brut ne l'est pas : « 24:00 » n'est pas
 * une heure. Le modulo la nomme « 00:00 », ce qu'elle est.
 */
export function hourLabel(hour: number): string {
  return `${String(hour % HOURS_PER_DAY).padStart(2, '0')}:00`
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

/**
 * « Semaine 31 · 2026 » — le repère par lequel le terrain et le bureau se parlent, et que la
 * date seule ne donne pas : un rendez-vous se déplace « en semaine 32 ».
 *
 * ⚠️ Numérotage **ISO 8601**, qui se calcule et ne s'approxime pas : une semaine porte le
 * numéro et l'**année de son jeudi**. Le 1ᵉʳ janvier 2027 tombe donc en semaine 53 de 2026, et
 * l'annoncer « semaine 53 · 2027 » nommerait une semaine qui n'existe pas.
 */
export function weekEyebrow(day: string): string {
  const date = dateOfDay(day)

  if (date === null) return ''

  // Le jeudi de la semaine du jour, puis celui de la semaine 1 — celle qui porte le 4 janvier.
  const thursday = thursdayOf(date)
  const first = thursdayOf(new Date(thursday.getFullYear(), 0, 4))
  // Arrondi, et non division entière : deux jeudis à minuit local sont séparés d'un nombre
  // entier de semaines **à une heure près**, deux dimanches de changement d'heure par an.
  const week = 1 + Math.round((thursday.getTime() - first.getTime()) / WEEK_MS)

  return `Semaine ${week} · ${thursday.getFullYear()}`
}

/** Le jeudi de la semaine d'une date — `getDay()` rendant 0 le dimanche, le décalage ramène
    lundi à 0. */
function thursdayOf(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() - ((date.getDay() + 6) % 7) + 3,
  )
}
